import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import Toggle from '@amzn/meridian/toggle';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import { AssignmentAssociate, PathType, ProcessPath, StaffingMode, isEmptySlot } from '../models';
import WorkstationCard from './WorkstationCard';
import AssociateCard from './AssociateCard';
import AddAssociateSlot from './AddAssociateSlot';
import './ProcessPathSection.scss';

export interface ProcessPathSectionProps {
  /** The process path (function) to render. */
  processPath: ProcessPath;
  /** Whether the card body is expanded. */
  expanded: boolean;
  /** Fires with the path's `fclmFunctionId` when the chevron is toggled. */
  onToggleExpanded: (fclmFunctionId: string) => void;
  /**
   * The unassigned associate pool, forwarded to line lanes so their empty
   * slots can offer an inline "Add associate" typeahead. Only used for
   * line-staffed (PROCESS_LINES) paths.
   */
  unassignedAssociates?: AssignmentAssociate[];
  /**
   * Fires when the user fills an empty line slot: `(associateId,
   * workstationId, slotIndex)`. When omitted, empty slots stay read-only.
   */
  onAssignToSlot?: (associateId: string, workstationId: string, slotIndex: number) => void;
  /**
   * Fires when the user fills an empty non-workstation (pool) slot:
   * `(associateId, fclmFunctionId, slotIndex)`. Used for no-lane functions
   * whose planned positions arrive as placeholder entries in
   * `nonWorkstationAssociates`. When omitted, those empty slots stay read-only.
   */
  onAssignToNonWorkstationSlot?: (
    associateId: string,
    fclmFunctionId: string,
    slotIndex: number,
  ) => void;
  /**
   * Fires when the user unassigns an assigned associate (from a line slot or a
   * pool). Receives the associate's id. Forwarded to the line lanes and pool
   * cards; when omitted, occupied entries stay read-only.
   */
  onUnassign?: (associateId: string) => void;
  /**
   * Fires when the user moves an assigned associate to a new location.
   * Receives the associate's id. Forwarded to the line lanes and pool cards;
   * when omitted, no move affordance is shown.
   */
  onMove?: (associateId: string) => void;
  /**
   * Fires when the user views an assigned associate's decision (the info ℹ️
   * action). Receives the associate's id. Forwarded to the line lanes and pool
   * cards; when omitted, no info affordance is shown.
   */
  onViewDecision?: (associateId: string) => void;
  /**
   * Read-only mode (e.g. the published / history view). Forwarded to every
   * {@link WorkstationCard} and applied to the pool body so no mutating
   * affordance renders: line and pool empty slots show the neutral
   * "Unassigned" label instead of an inline "Add associate" typeahead, and
   * unassign/move actions are suppressed. The view-decision (ℹ️) action is
   * unaffected — it is itself read-only. Defaults to false.
   */
  readOnly?: boolean;
}

type StatColor = 'success' | 'error' | undefined;

interface HeaderStat {
  key: string;
  label: string;
  value: number;
  color: StatColor;
}

/**
 * A collapsible process-path card for the redesigned Station Assignment
 * screen. The header shows the path name, a Direct/Indirect tag, a compact
 * metrics strip, and — for workstation-staffed functions — a "Show WS IDs"
 * toggle. The body adapts to how the function is staffed:
 *
 *   - Line-staffed (`PROCESS_LINES` with lanes) → a grid of
 *     {@link WorkstationCard} lanes.
 *   - Otherwise (DEPARTMENT pools, or indirect functions) → a wrapped pool of
 *     read-only {@link AssociateCard}s. Pool associates come from
 *     `nonWorkstationAssociates` when present, otherwise from the workstations'
 *     assigned associates.
 *
 * Only metrics the real API sends are shown — Assigned, Actual and Delta from
 * `metrics`, plus Capacity (summed workstation capacity) when the function has
 * workstations. Delta is coloured by sign. "Show WS IDs" and Capacity appear
 * only when the function has workstations, since only workstation-staffed
 * associates carry a per-station id — `nonWorkstationAssociates` do not.
 * Read-only for now; assignment interactions land in a later slice.
 *
 * Built from Meridian primitives (Box/Row/Column/Text/Button/Icon/Tag/Toggle)
 * plus the WorkstationCard and AssociateCard children.
 */
const ProcessPathSection: React.FC<ProcessPathSectionProps> = ({
  processPath,
  expanded,
  onToggleExpanded,
  unassignedAssociates,
  onAssignToSlot,
  onAssignToNonWorkstationSlot,
  onUnassign,
  onMove,
  onViewDecision,
  readOnly = false,
}) => {
  const [showWorkstationIds, setShowWorkstationIds] = useState<boolean>(false);

  const isDirect = processPath.pathType === PathType.DIRECT;
  const chevronTokens = expanded ? chevronDownSmallTokens : chevronRightSmallTokens;

  const hasWorkstations = processPath.workstations.length > 0;
  const laneBased =
    processPath.staffingMode === StaffingMode.PROCESS_LINES && hasWorkstations;

  const capacity = useMemo(
    () => processPath.workstations.reduce((sum, ws) => sum + ws.capacity, 0),
    [processPath.workstations],
  );

  /**
   * Entries for the pool (non-lane) body. Prefer the explicit
   * `nonWorkstationAssociates` list; otherwise flatten the workstations'
   * assigned associates (the DEPARTMENT-pool case, whose associates still
   * carry a station id).
   *
   * Each entry keeps enough identity to fill its empty slot precisely with the
   * same {@link AddAssociateSlot} typeahead the lanes use: a workstation-backed
   * entry carries its `workstationId` + local slot index; a non-workstation
   * entry carries its index within `nonWorkstationAssociates`.
   */
  const poolEntries: Array<{
    associate: AssignmentAssociate;
    workstationId?: string;
    localIndex?: number;
    nonWorkstationIndex?: number;
  }> = useMemo(() => {
    if (laneBased) return [];
    const nonWorkstation = processPath.nonWorkstationAssociates ?? [];
    if (nonWorkstation.length > 0) {
      return nonWorkstation.map((associate, nonWorkstationIndex) => ({
        associate,
        nonWorkstationIndex,
      }));
    }
    const entries: Array<{
      associate: AssignmentAssociate;
      workstationId?: string;
      localIndex?: number;
    }> = [];
    processPath.workstations.forEach((ws) => {
      ws.assignedAssociates.forEach((associate, localIndex) => {
        entries.push({ associate, workstationId: ws.workstationId, localIndex });
      });
    });
    return entries;
  }, [laneBased, processPath.nonWorkstationAssociates, processPath.workstations]);

  const { actual, assigned, delta } = processPath.metrics;
  const stats: HeaderStat[] = [
    { key: 'assigned', label: 'Assigned', value: assigned, color: undefined },
    ...(hasWorkstations
      ? [{ key: 'capacity', label: 'Capacity', value: capacity, color: undefined } as HeaderStat]
      : []),
    { key: 'actual', label: 'Actual', value: actual, color: undefined },
    {
      key: 'delta',
      label: 'Delta',
      value: delta,
      color: delta < 0 ? 'error' : delta > 0 ? 'success' : undefined,
    },
  ];

  // Keep the header `widths` count aligned with the rendered children: the
  // trailing toggle cell only exists for workstation-staffed functions.
  const headerWidths = hasWorkstations
    ? ['fit', 'fit', 'fit', 'fill', 'fit']
    : ['fit', 'fit', 'fit', 'fill'];

  return (
    <Box
      className="assignments-process-path"
      backgroundColor="subdued"
      data-testid={`process-path-${processPath.fclmFunctionId}`}
    >
      <Row
        className="assignments-process-path__header"
        backgroundColor="highlight"
        alignmentVertical="center"
        spacing="300"
        widths={headerWidths}
      >
        <Button
          type="icon"
          size="small"
          label={expanded ? 'Collapse' : 'Expand'}
          onClick={() => onToggleExpanded(processPath.fclmFunctionId)}
          data-testid={`process-path-toggle-${processPath.fclmFunctionId}`}
        >
          <Icon tokens={chevronTokens} />
        </Button>

        <Text type="h200">{processPath.processPathName}</Text>

        <Tag type={isDirect ? 'neutral' : 'theme'}>
          {isDirect ? 'Direct' : 'Indirect'}
        </Tag>

        <Row
          className="assignments-process-path__metrics"
          spacing="400"
          alignmentVertical="center"
        >
          {stats.map((stat) => (
            <Column
              key={stat.key}
              spacing="none"
              alignmentHorizontal="center"
              data-testid={`process-path-metric-${stat.key}`}
            >
              <Text type="b200" color={stat.color}>
                {stat.value}
              </Text>
              <Text
                type="b100"
                color="secondary"
                className="assignments-process-path__metric-label"
              >
                {stat.label}
              </Text>
            </Column>
          ))}
        </Row>

        {hasWorkstations && (
          <Box data-testid={`process-path-wsids-${processPath.fclmFunctionId}`}>
            <Toggle
              checked={showWorkstationIds}
              onChange={setShowWorkstationIds}
              size="small"
            >
              Show WS IDs
            </Toggle>
          </Box>
        )}
      </Row>

      {expanded &&
        (laneBased ? (
          <Box className="assignments-process-path__grid" data-testid="process-path-grid">
            {processPath.workstations.map((workstation) => (
              <WorkstationCard
                key={workstation.workstationId}
                workstation={workstation}
                showWorkstationIds={showWorkstationIds}
                unassignedAssociates={unassignedAssociates}
                onAssignToSlot={onAssignToSlot}
                onUnassign={onUnassign}
                onMove={onMove}
                onViewDecision={onViewDecision}
                readOnly={readOnly}
              />
            ))}
          </Box>
        ) : (
          <Box className="assignments-process-path__pool" data-testid="process-path-pool">
            {poolEntries.map(
              ({ associate, workstationId, localIndex, nonWorkstationIndex }, index) => {
                if (!isEmptySlot(associate)) {
                  return (
                    <AssociateCard
                      key={associate.associateId}
                      associate={associate}
                      showWorkstationId={showWorkstationIds}
                      onUnassign={readOnly ? undefined : onUnassign}
                      onMove={readOnly ? undefined : onMove}
                      onViewDecision={onViewDecision}
                    />
                  );
                }

                const hasPool = (unassignedAssociates?.length ?? 0) > 0;

                // A workstation-backed empty slot fills via `onAssignToSlot`
                // (targets a station); a non-workstation empty slot fills via
                // `onAssignToNonWorkstationSlot` (targets the function's
                // nonWorkstationAssociates list). Inline add is offered only
                // when a pool exists and WS IDs aren't being shown.
                const workstationAdd =
                  !readOnly &&
                  !showWorkstationIds &&
                  hasPool &&
                  !!onAssignToSlot &&
                  workstationId != null &&
                  localIndex != null;
                const nonWorkstationAdd =
                  !readOnly &&
                  !showWorkstationIds &&
                  hasPool &&
                  !!onAssignToNonWorkstationSlot &&
                  nonWorkstationIndex != null;

                if (workstationAdd || nonWorkstationAdd) {
                  const dataTestId = workstationAdd
                    ? `pool-add-${workstationId}-${localIndex}`
                    : `pool-add-nw-${processPath.fclmFunctionId}-${nonWorkstationIndex}`;
                  const handleSelect = (associateId: string) => {
                    if (workstationAdd) {
                      (onAssignToSlot as NonNullable<ProcessPathSectionProps['onAssignToSlot']>)(
                        associateId,
                        workstationId as string,
                        localIndex as number,
                      );
                    } else {
                      (
                        onAssignToNonWorkstationSlot as NonNullable<
                          ProcessPathSectionProps['onAssignToNonWorkstationSlot']
                        >
                      )(associateId, processPath.fclmFunctionId, nonWorkstationIndex as number);
                    }
                  };
                  return (
                    <Box
                      key={associate.assignedWorkstationId ?? `empty-${index}`}
                      className="assignments-process-path__pool-add"
                      data-testid="pool-slot-empty"
                    >
                      <AddAssociateSlot
                        unassignedAssociates={unassignedAssociates as AssignmentAssociate[]}
                        onSelect={handleSelect}
                        dataTestId={dataTestId}
                      />
                    </Box>
                  );
                }

                return (
                  <Box
                    key={associate.assignedWorkstationId ?? `empty-${index}`}
                    className="assignments-process-path__pool-empty"
                    backgroundColor="subdued"
                    data-testid="pool-slot-empty"
                  >
                    <Text type="b100" color="secondary">
                      {showWorkstationIds && associate.assignedWorkstationId
                        ? associate.assignedWorkstationId
                        : readOnly
                        ? 'Unassigned'
                        : 'Add associate'}
                    </Text>
                  </Box>
                );
              },
            )}
          </Box>
        ))}
    </Box>
  );
};

export default ProcessPathSection;

import './ProcessPathSection.scss';